import { Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ValidationErrorComponent } from "@controls/validation-error/validation-error.component";
import { SigningBillingContactBody } from "@models/billing-signing/signing-billing-contact-body";
import { UpdateBillingContactModel } from '@models/billing-signing/update-billing-contact-model';
import { Contact } from '@models/common/contact';
import { Company } from "@models/companies/company";
import { Relationship } from '@models/relationships/relationship';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { Guid } from '@shared/utils/guid.helper';
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { ToastrService } from "ngx-toastr";
import { map, Observable, take } from "rxjs";
import { ContactsSelectComponent } from "../../../../forms/controls/contacts-select/contacts-select.component";

interface IBillingSigningFormControls {
  areContactsSame: FormControl<boolean>;
  signingContactId: FormControl<string | null>;
  billingContactId: FormControl<string | null>;
}

type IBillingSigningFormGroup = FormGroup<IBillingSigningFormControls>;

@Component({
  selector: 'app-billing-signing-contact',
  standalone: true,
  templateUrl: 'billing-signing-contact-modal.component.html',
  imports: [
    ReactiveFormsModule,
    CommonModalWrapperComponent,
    CheckboxComponent,
    ContactsSelectComponent,
    ValidationErrorComponent
  ],
  providers: [],
})
export class BillingSigningContactModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private companiesApiService = inject(CompaniesApiService);
  private toastr = inject(ToastrService);

  @Input() header = 'Edit Billing & Signing contact details';
  @Input() showActionRequiredMessage = false;
  @Input() billingContactControlLabel = 'Billing Contact';
  @Input() closeBtnText = 'Cancel';
  @Input() company!: Company;
  @Input() directors: Relationship[] = [];
  @Input() signingContact: Contact | null = null;
  @Input() billingContact: Contact | null = null;
  @Input() onlyBillingContact = false;
  @Input() cancelAction: Observable<unknown> | null = null;

  readonly customErrors = { selectAtLeastOneContact: `At least one contact must be selected.` };

  isLoading = false;
  contacts: Contact[] = [];
  billingContacts: Contact[] = [];

  form = new FormGroup<IBillingSigningFormControls>({
    areContactsSame: new FormControl<boolean>(true, { nonNullable: true }),
    signingContactId: new FormControl<string | null>(null),
    billingContactId: new FormControl<string | null>(null),
  });

  ngOnInit() {
    this.form.patchValue({
      areContactsSame: Boolean(this.signingContact && this.signingContact?.contactId == this.billingContact?.contactId),
      signingContactId: this.signingContact?.contactId ?? null,
      billingContactId: this.billingContact?.contactId ?? null,
    });

    this.addBillingContactRequiredValidator();
    this.addAtLeastOneContactRequiredValidator();
    this.fillContacts();
    this.turnOffSigningPart();
  }

  fillContacts(): void {
    const contactsMap = new Map<string, Contact>();

    this.company.currentOfficers
      .filter((relationship) => !relationship.end)
      .flatMap((relationship) => relationship.individual?.contacts)
      .forEach((contact) => {
        if (contact?.contactId) {
          contactsMap.set(contact?.contactId, contact);
        }
      });

    const contacts: Contact[] = [...contactsMap.values()];
    this.contacts = contacts;
    this.billingContacts = contacts.slice();

    if (this.billingContact && !this.billingContacts.find((contact) => contact.contactId == this.billingContact?.contactId)) {
      this.billingContacts.push(this.billingContact);
    }

    if (this.onlyBillingContact && !this.form.controls.billingContactId.value && this.billingContacts.length) {
      this.form.controls.billingContactId.setValue(this.billingContacts[0].contactId);
    }
  }

  onSigningContactSelected(contact: Contact | null): void {
    if (this.form.controls.areContactsSame.value) {
      this.form.controls.billingContactId.setValue(contact?.contactId ?? null);
    }
  }

  onContactCreated(contact: Contact, control: FormControl<string | null>): void {
    this.contacts.push(contact);
    this.billingContacts.push(contact);
    control.setValue(contact.contactId);
  }

  onBillingContactCrated(contact: Contact): void {
    this.billingContacts.push(contact);
    this.form.controls.billingContactId.setValue(contact.contactId);
  }

  confirm(): void {
    if (this.isFormInvalid) {
      this.form.markAllAsTouched();
      this.toastr.error('Please fill required fields', 'Error');
      return;
    }

    const billingContact = this.billingContacts.find((contact) => contact.contactId == this.form.controls.billingContactId.value) ?? null;
    const billingContactRelationshipId = this.directors.find((relationship) => relationship.individualId == billingContact?.individualId)?.relationshipId ?? null;

    const request = this.onlyBillingContact
      ? this.updateBillingContact(billingContact!, billingContactRelationshipId)
      : this.updateSigningBillingContact(billingContact, billingContactRelationshipId);

    request
      .subscribe({
        next: (body) => {
          this.activeModal.close(body);
          this.form.enable();
          this.isLoading = false;
          this.toastr.success('Contacts saved successfully', 'Success');
        },
        error: (error) => {
          console.error('[BillingSigningContactComponent.updateSigningBillingContact]', error);
          this.form.enable();
          this.turnOffSigningPart();
          this.isLoading = false;
          this.toastr.error('Error happened while saving contacts', 'Error');
        }
      });
  }

  updateSigningBillingContact(billingContact: Contact | null, billingContactRelationshipId: string | null): Observable<unknown> {
    const signingContact = this.contacts.find((contact) => contact.contactId == this.form.controls.signingContactId.value) ?? null;
    const signingContactRelationshipId = this.directors.find((relationship) => relationship.individualId == signingContact?.individualId)?.relationshipId ?? null;

    const body = new SigningBillingContactBody({
      signingContact,
      signingContactRelationshipId,
      billingContact,
      billingContactRelationshipId,
      signingContactIsBillingContact: this.form.controls.areContactsSame.value ?? false
    });

    if (!billingContactRelationshipId) {
      body.billingContactRelationshipId = Guid.EmptyGuid;
      body.billingContact = {
        firstName: billingContact?.firstName,
        lastName: billingContact?.lastName,
        email: billingContact?.email,
        phone: billingContact?.phone,
        carbonCopy: billingContact?.carbonCopy ?? '',
        blindCarbonCopy: billingContact?.blindCarbonCopy ?? '',
        nickname: billingContact?.nickname ?? '',
      } as Contact;
    }

    return this.companiesApiService.updateSigningBillingContact(this.company.entityId, body)
      .pipe(map(() => body));
  }

  updateBillingContact(billingContact: Contact, billingContactRelationshipId: string | null): Observable<unknown> {
    const updateBillingContactModel: UpdateBillingContactModel = {
      billingContact,
      billingContactRelationshipId: billingContactRelationshipId ?? Guid.EmptyGuid
    };
    return this.companiesApiService.setBillingContact(this.company.entityId, updateBillingContactModel)
      .pipe(map(() => ({ billingContact, billingContactRelationshipId })));
  }

  close(): void {
    if (this.cancelAction) {
      setControlDisabled(this.form);
      this.isLoading = true;

      this.cancelAction
        .pipe(take(1))
        .subscribe({
          next: () => this.activeModal.dismiss(),
          error: () => this.activeModal.dismiss(),
        });

      return;
    }

    this.activeModal.dismiss();
  }

  turnOffSigningPart(): void {
    if (this.onlyBillingContact) {
      setControlDisabled(this.form.controls.areContactsSame);
      setControlDisabled(this.form.controls.signingContactId);
    }
  }

  addBillingContactRequiredValidator(): void {
    if (this.showActionRequiredMessage) {
      this.form.controls.billingContactId.addValidators(Validators.required);
    }
  }

  addAtLeastOneContactRequiredValidator(): void {
    const atLeastOneContactRequiredValidator: ValidatorFn = (control: AbstractControl) => {
      const form = control as IBillingSigningFormGroup;
      const isBillingContactIdValid = Boolean(form.controls.billingContactId.enabled && form.controls.billingContactId.value);
      const isSigningContactIdValid = Boolean(form.controls.signingContactId.enabled && form.controls.signingContactId.value);

      return isBillingContactIdValid || isSigningContactIdValid
        ? null
        : { selectAtLeastOneContact: true };
    };

    this.form.addValidators(atLeastOneContactRequiredValidator);
  }

  get isFormInvalid(): boolean {
    if (this.onlyBillingContact) {
      return this.form.controls.billingContactId.invalid;
    }

    return this.form.invalid;
  }

  get signingContactLabel(): string {
    return this.form.controls.areContactsSame.value
      ? 'Signing and Billing Contact'
      : 'Signing Contact';
  }
}
